import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, LogOut, Hourglass, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useMidtransSnap, formatRupiah } from "@/lib/midtrans";
import PaymentLogos from "@/components/PaymentLogos";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS_META = [
  { id: "bulan1", name: "Paket 1 Bulan", period: "/bulan",
    features: ["Akses semua fitur", "Template undangan standar", "Manajemen tamu, budget, vendor",
               "RSVP & buku tamu", "Berlaku 30 hari"] },
  { id: "bulan6", name: "Paket 6 Bulan", period: "/6 bulan", highlight: true, badge: "Paling Populer",
    features: ["Semua fitur Paket 1 Bulan", "Template undangan standar", "Berlaku 180 hari", "Prioritas dukungan"] },
  { id: "premium", name: "Paket Premium", period: "/lifetime", badge: "Full Akses",
    features: ["Semua fitur", "15 tema premium (Wekita-style)", "Template standar + premium",
               "Akses selamanya", "Prioritas fitur baru", "Dukungan langsung"] },
];

export default function PaketPage() {
  const { user, refreshUser, logout } = useAuth();
  const nav = useNavigate();
  const [busyId, setBusyId] = useState("");
  const [prices, setPrices] = useState({ bulan1: 99000, bulan6: 399000, premium: 799000 });
  const [plansMeta, setPlansMeta] = useState({});
  const [popularPlan, setPopularPlan] = useState("bulan6");
  const [clientKey, setClientKey] = useState("");
  const [isProd, setIsProd] = useState(false);
  const currentPlan = user?.plan;
  const currentStatus = user?.plan_status;
  const pendingPlan = user?.plan_requested;
  const pay = useMidtransSnap(clientKey, isProd);

  useEffect(() => {
    axios.get(`${API}/public/plan-prices`).then((r) => {
      setPrices(r.data.prices); setPlansMeta(r.data.plans || {}); setPopularPlan(r.data.popular_plan || "bulan6");
      setClientKey(r.data.midtrans_client_key); setIsProd(r.data.is_production);
    }).catch(() => {});
  }, []);

  // Redirect immediately (no flash) if plan is already active.
  if (currentStatus === "active") return <Navigate to="/app" replace />;

  const startPayment = async (planId) => {
    setBusyId(planId);
    try {
      const { data } = await api.post("/midtrans/snap-token", { plan: planId });
      toast.info(`Membuka pembayaran ${formatRupiah(data.amount)}...`);
      pay(data.token, {
        onSuccess: async () => {
          toast.success("Pembayaran berhasil! Paket sedang diaktifkan...");
          setTimeout(async () => { await refreshUser(); nav("/payment/success"); }, 2000);
        },
        onPending: () => { toast.info("Pembayaran menunggu konfirmasi."); nav("/payment/pending"); },
        onError: () => { toast.error("Pembayaran gagal. Silakan coba lagi."); nav("/payment/failed"); },
        onClose: () => { toast.info("Pembayaran dibatalkan."); setBusyId(""); },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memulai pembayaran");
      setBusyId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0a08] px-5 py-12 text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="paket-page">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Inter:wght@400;500;600;700&display=swap" />
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#c9a35c]" style={{ fontFamily: "'Inter', sans-serif" }}>Pilih Paket</p>
            <h1 className="mt-2 text-4xl italic sm:text-5xl" style={{ fontFamily: "'Great Vibes', cursive", color: "#f7efe4" }}>Aktifkan Akses</h1>
            <p className="mt-2 text-sm text-neutral-400" style={{ fontFamily: "'Inter', sans-serif" }}>
              Hai <b className="text-[#e6c98a]">{user?.username}</b> — pilih paket & bayar via Midtrans.
            </p>
          </div>
          <Button onClick={logout} data-testid="paket-logout" variant="outline" className="gap-2 rounded-full border-[#c9a35c66] text-[#e6c98a]">
            <LogOut className="h-4 w-4" /> Keluar
          </Button>
        </div>

        {currentStatus === "expired" && (
          <div className="mb-8 rounded-2xl border border-red-400/40 bg-red-400/5 p-5" data-testid="paket-expired-banner">
            <p className="font-semibold text-red-300" style={{ fontFamily: "'Inter', sans-serif" }}>Paket Anda Berakhir</p>
            <p className="mt-1 text-sm text-red-200/80" style={{ fontFamily: "'Inter', sans-serif" }}>
              Silakan pilih paket & lakukan pembayaran untuk memperpanjang akses.
            </p>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          {PLANS_META.map((p, i) => {
            const price = prices[p.id];
            const isActive = currentPlan === p.id && currentStatus === "active";
            const isPopular = p.id === popularPlan;
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                data-testid={`paket-card-${p.id}`} data-popular={isPopular ? "true" : "false"}
                className={`relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 ${isPopular ? "border-2 border-[#c9a35c] bg-gradient-to-b from-[#1a120e] via-[#1e150f] to-[#1a120e] shadow-[0_0_50px_-15px_#c9a35c66] md:scale-[1.03]" : "border-[#c9a35c33] bg-[#1a120e]"}`}>
                {isPopular && <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#e6c98a] to-transparent" />}
                {isPopular && (
                  <Badge data-testid={`paket-popular-badge-${p.id}`} className="self-start rounded-full border-0 bg-gradient-to-r from-[#c9a35c] to-[#e6c98a] px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#0f0a08]"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    <Crown className="mr-1 h-3 w-3" /> Populer
                  </Badge>
                )}
                {!isPopular && p.badge && (
                  <Badge className="self-start rounded-full border border-[#c9a35c66] bg-[#c9a35c11] px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#e6c98a]"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {p.id === "premium" && <Crown className="mr-1 h-3 w-3" />}
                    {p.badge}
                  </Badge>
                )}
                <h3 className="mt-4 text-2xl font-semibold" style={{ fontFamily: "'Inter', sans-serif", color: "#f7efe4" }}>{p.name}</h3>
                <div className="mt-3 flex flex-col items-start gap-1">
                  {plansMeta[p.id]?.discount_active && plansMeta[p.id]?.original_price && (
                    <div className="flex items-center gap-2">
                      <span className="text-lg text-neutral-500 line-through" style={{ fontFamily: "'Inter', sans-serif" }}>{formatRupiah(plansMeta[p.id].original_price)}</span>
                      <Badge className="rounded-full border-0 bg-red-500/20 px-2 py-0 text-[9px] uppercase tracking-wider text-red-300" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {Math.round((1 - plansMeta[p.id].price / plansMeta[p.id].original_price) * 100)}% OFF
                      </Badge>
                    </div>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold" style={{ fontFamily: "'Inter', sans-serif", color: isPopular ? "#e6c98a" : "#f7efe4" }} data-testid={`price-${p.id}`}>{formatRupiah(price)}</span>
                    <span className="text-sm text-neutral-500" style={{ fontFamily: "'Inter', sans-serif" }}>{p.period}</span>
                  </div>
                  {plansMeta[p.id]?.discount_active && plansMeta[p.id]?.discount_type === "orders" && (
                    <p className="text-[10px] text-amber-400" style={{ fontFamily: "'Inter', sans-serif" }}>🔥 Sisa {plansMeta[p.id].discount_remaining_orders} kuota diskon</p>
                  )}
                  {plansMeta[p.id]?.discount_active && plansMeta[p.id]?.discount_type === "time" && plansMeta[p.id]?.discount_end_at && (
                    <p className="text-[10px] text-amber-400" style={{ fontFamily: "'Inter', sans-serif" }}>⏰ Berakhir {new Date(plansMeta[p.id].discount_end_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  )}
                </div>
                <div className="my-5 h-px bg-gradient-to-r from-transparent via-[#c9a35c44] to-transparent" />
                <ul className="flex-1 space-y-2.5">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-neutral-300" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#e6c98a]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button data-testid={`paket-cta-${p.id}`} onClick={() => startPayment(p.id)} disabled={isActive || busyId === p.id}
                  className={`mt-6 w-full gap-2 rounded-full py-6 text-sm font-semibold ${p.highlight ? "text-[#0f0a08]" : "border border-[#c9a35c66] bg-transparent text-[#e6c98a] hover:bg-[#c9a35c11]"} disabled:opacity-60`}
                  style={p.highlight ? { background: "linear-gradient(90deg,#c9a35c,#e6c98a)", fontFamily: "'Inter', sans-serif" } : { fontFamily: "'Inter', sans-serif" }}>
                  <CreditCard className="h-4 w-4" />
                  {isActive ? "Sedang Aktif" : (busyId === p.id ? "Membuka Snap..." : "Bayar Sekarang")}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10">
          <PaymentLogos variant="dark" testid="paket-payment-logos" />
        </div>
        <p className="mt-4 text-center text-xs text-neutral-500" style={{ fontFamily: "'Inter', sans-serif" }}>
          Paket aktif otomatis setelah pembayaran dikonfirmasi.
        </p>
      </div>
    </div>
  );
}
